import React, { useState, useEffect } from "react";
import Avatar from "@material-ui/core/Avatar";
import { Button } from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";

import Grid from "@material-ui/core/Grid";
import LockIcon from "@mui/icons-material/Lock";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import * as Yup from "yup";
import { verifyForgotPassword } from "../../../../service/endpoint-manager";
import { useSnackbar } from "notistack";
import Loading from "../../Shared/Loading";
import ButtonTextLoading from "../../Shared/ButtonTextLoading";
import { useNavigate } from "react-router-dom";
import IconButton from "@material-ui/core/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useFormik } from "formik";

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: "#3f51b5",
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },

  submit: {
    margin: theme.spacing(3, 0, 2),
  },

  header: {
    width: "100%",
    display: "flex",
    // justifyContent: 'center',
    alignItems: "center",
    textAlign: "center",
  },

  backButtonStyles: {
    width: "20%",
  },

  pageHeading: {
    width: "80%",
    textAlign: "left",
    paddingLeft: "1.5rem",
    // border: '1px solid green',
  },
}));

export default function ResetPassword({ setVerificationCodeSent, userName }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      verificationCode: "",
      newpassword: "",
      confirmNewPassword: "",
    },
    onSubmit: async function (values) {
      setIsSubmitting(true);
      let result = null;

      result = await verifyForgotPassword(
        userName,
        values.verificationCode,
        values.newpassword
      );

      // show a success message in case of a successful response from the API (verification successful)
      if (result?.data?.status === true && result?.data?.body === "SUCCESS") {
        enqueueSnackbar("Password Updated Successfully!", {
          variant: "success",
        });
        navigate("/");
      } else {
        // else show an error message
        enqueueSnackbar("Something went wrong. Please try again", {
          variant: "error",
        });
      }

      setIsSubmitting(false);
    },

    // validation schema for formik using yup
    validationSchema: Yup.object({
      newpassword: Yup.string().min(8).required(),
      confirmNewPassword: Yup.string().min(8).required(),
      verificationCode: Yup.string().required(),
    }),
  });

  // this useEffect checks to make sure both password and confirmPassword values are supplied
  //and match - sets state accordingly
  useEffect(() => {
    if (
      formik.values.newpassword.length > 0 &&
      formik.values.confirmNewPassword.length > 0 &&
      formik.values.newpassword !== formik.values.confirmNewPassword
    ) {
      setPasswordsMatch(false);
    } else {
      setPasswordsMatch(true);
    }
  }, [formik.values.newpassword, formik.values.confirmNewPassword]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          <LockIcon />
        </Avatar>
        <section className={classes.header}>
          <IconButton
            className={classes.backButtonStyles}
            onClick={() => {
              setVerificationCodeSent(false);
            }}
          >
            <ArrowBackIcon color="#000" />
          </IconButton>
          <Typography
            component="h1"
            variant="h5"
            className={classes.pageHeading}
          >
            Reset Password
          </Typography>
        </section>
        <form
          onSubmit={formik.handleSubmit}
          className={classes.form}
          noValidate
        >
          <Grid container spacing={2}>
            {/* verification Code */}
            <Grid item xs={12}>
              <TextField
                error={
                  formik.touched.verificationCode &&
                  formik.errors.verificationCode
                }
                required
                fullWidth
                id="verificationCode"
                label="Please enter your verification code"
                type="string"
                name="verificationCode"
                autoComplete="verificationCode"
                variant="outlined"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.verificationCode}
              />
              {
                <div
                  style={{
                    color: "red",
                    fontSize: "0.6rem",
                    visibility:
                      formik.touched.verificationCode &&
                      formik.errors.verificationCode
                        ? "visible"
                        : "hidden",
                  }}
                >
                  {formik.errors.verificationCode ||
                    "Verification code is required"}
                </div>
              }
            </Grid>

            {/* password */}
            <Grid item xs={12}>
              <TextField
                error={formik.touched.newpassword && formik.errors.newpassword}
                required
                fullWidth
                id="password"
                label="Please enter new password"
                name="newpassword"
                type="password"
                autoComplete="password"
                variant="outlined"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.newpassword}
              />
              {
                <div
                  style={{
                    color: "red",
                    fontSize: "0.6rem",
                    visibility:
                      formik.touched.newpassword && formik.errors.newpassword
                        ? "visible"
                        : "hidden",
                  }}
                >
                  {formik.errors.newpassword || "Password is required"}
                </div>
              }
            </Grid>

            {/* confirm password */}
            <Grid item xs={12}>
              <TextField
                error={
                  formik.touched.confirmNewPassword &&
                  formik.errors.confirmNewPassword
                }
                required
                fullWidth
                id="password"
                type="password"
                label="Please enter your password again"
                name="confirmNewPassword"
                autoComplete="password"
                variant="outlined"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.confirmNewPassword}
              />
              {
                <div
                  style={{
                    color: "red",
                    fontSize: "0.6rem",
                    visibility:
                      formik.touched.confirmNewPassword &&
                      formik.errors.confirmNewPassword
                        ? "visible"
                        : "hidden",
                  }}
                >
                  {formik.errors.confirmNewPassword ||
                    "Password confirmation is required"}
                </div>
              }
            </Grid>

            {/* passwords match */}
            {
              <div
                style={{
                  color: "red",
                  fontSize: "0.6rem",
                  visibility: !passwordsMatch ? "visible" : "hidden",
                }}
              >
                Passwords don't match
              </div>
            }
          </Grid>

          {/* submit button */}
          <Button
            type="submit"
            // button will be disabled if form has errors
            disabled={
              Object.keys(formik.errors).length > 0 ||
              !formik.values.newpassword ||
              !formik.values.confirmNewPassword ||
              !formik.values.verificationCode ||
              !passwordsMatch
            }
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
          >
            <ButtonTextLoading
              isSubmitting={isSubmitting}
              body={"Reset Password"}
            />
          </Button>
        </form>
      </div>
    </Container>
  );
}
